import type { PullRequest, PullRequestReview } from '../util';

import { Fragment } from 'react';
import { css, html } from 'react-strict-dom';
import { Avatar, Stack, Text } from 'src/components';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';

import { getPullRequestStateVisual, getReviewVisual } from '../util';
import PullRequestStateIcon from './PullRequestStateIcon';

type Props = {
  pullRequests: ReadonlyArray<PullRequest>;
};

function ReviewChip({ review }: { review: PullRequestReview }) {
  const reviewerName = review.reviewer
    ? `${review.reviewer.firstName} ${review.reviewer.lastName}`
    : review.reviewerGithubUsername;
  const visual = getReviewVisual(review);

  return (
    <html.div
      style={[Style.row, Style.alignCenter, styles.reviewChip]}
      aria-label={`${reviewerName} ${visual.label}`}
    >
      <Avatar user={review.reviewer} size={24} />
      <html.div style={[Style.row, Style.centerBoth, styles.reviewIcon]}>
        <PullRequestStateIcon visual={visual} />
      </html.div>
    </html.div>
  );
}

function PullRequestRow({ pullRequest }: { pullRequest: PullRequest }) {
  const visual = getPullRequestStateVisual(pullRequest);
  const authorName = pullRequest.author
    ? `${pullRequest.author.firstName} ${pullRequest.author.lastName}`
    : pullRequest.authorGithubUsername ?? 'Unknown author';

  return (
    <Stack gap={1}>
      <html.div style={[Style.row, Style.alignStart, styles.titleRow]}>
        <html.div style={styles.avatarWrap} aria-label={authorName}>
          <Avatar user={pullRequest.author} size={32} />
        </html.div>
        <html.a
          href={pullRequest.url}
          target="_blank"
          rel="noopener noreferrer"
          style={[Style.fill, styles.titleLink]}
        >
          <Text typeface="body.small.medium" color="neutral100" underline>
            #{pullRequest.number} {pullRequest.title}
          </Text>
        </html.a>
        <html.div style={styles.stateIcon}>
          <PullRequestStateIcon visual={visual} />
        </html.div>
      </html.div>

      {pullRequest.latestReviews.length > 0 && (
        <html.div style={[Style.row, Style.alignCenter, styles.reviewRow]}>
          {pullRequest.latestReviews.map((review) => (
            <ReviewChip key={review.id} review={review} />
          ))}
        </html.div>
      )}
    </Stack>
  );
}

export default function PullRequestsCard({ pullRequests }: Props) {
  if (pullRequests.length === 0) return null;

  return (
    <Stack gap={1.5} style={styles.card}>
      <Text typeface="body.regular" color="neutral80">
        Pull requests
      </Text>
      {pullRequests.map((pullRequest, i) => (
        <Fragment key={pullRequest.id}>
          {i > 0 && <html.hr style={styles.divider} />}
          <PullRequestRow pullRequest={pullRequest} />
        </Fragment>
      ))}
    </Stack>
  );
}

const styles = css.create({
  card: {
    backgroundColor: color.neutral10,
    borderRadius: 12,
    padding: 16,
  },
  divider: {
    width: '100%',
    height: 0,
    borderWidth: 0,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: color.neutral20,
    margin: 0,
  },
  titleRow: {
    gap: 8,
  },
  avatarWrap: {
    display: 'flex',
    flexShrink: 0,
  },
  titleLink: {
    minWidth: 0,
    textDecorationLine: 'none',
  },
  stateIcon: {
    display: 'flex',
    flexShrink: 0,
  },
  reviewRow: {
    gap: 8,
    flexWrap: 'wrap',
    paddingInlineStart: 40,
  },
  reviewChip: {
    gap: 4,
    backgroundColor: color.neutral20,
    borderRadius: 32,
    paddingInline: 8,
    height: 40,
  },
  reviewIcon: {
    width: 40,
    height: 40,
    flexShrink: 0,
  },
});
